import {Trans} from '@lingui/macro';
import React, {FC, useMemo, useReducer} from 'react';

import {HandlerOf} from '../../../../libs/shared/helpers/src/react/reactHelpers';
import {Button} from '../../../../libs/shared/ui/src/components/buttons/button';
import {ButtonVariantsEnum} from '../../../../libs/shared/ui/src/components/buttons/buttonConstants';
import {VisualSizesEnum} from '../../../../libs/shared/ui/src/styles/commonStyles';
import {useAppDispatch} from '../../../helpers/reduxHelpers';
import {ChannelTypesEnum} from '../../../models/api/channelCommonTypes';
import {ChannelModel} from '../../../models/api/channelModel';
import {InboxModel} from '../../../models/api/inboxModel';
import {SettingsInboxesUrl} from '../../../models/url/settingsUrlModel';
import {updateChannelAsyncAction} from '../../../store/data/channels/channelsActions';
import {SettingsPanelId} from '../../../store/panels/panelStateBase';
import {
  SettingsFooter,
  StyledSettingsFormContent,
  StyledSettingsFormField,
  StyledSettingsFormMain,
  StyledSettingsFormTextBox,
  StyledSettingsWrapper,
} from '../../common/settingsWrapper';
import {SettingsChannelWarningBanner} from '../settingsChannelWarningBanner';
import {SettingsInboxesChannelHeader} from '../settingsInboxesChannelHeader';
import {SettingsFrontChatFields} from './chat/settingsFrontChatFields';
import {SettingsChannelCustomFields} from './custom/settingsChannelCustomFields';
import {SettingsGmailFields} from './email/settingsGmailFields';
import {SettingsOffice365Fields} from './email/settingsOffice365Fields';
import {SettingsSmtpFields} from './email/settingsSmtpFields';
import {SettingsChannelSmoochFields} from './smooch/settingsChannelSmoochFields';
import {SettingsChannelTwilioFields} from './twilio/settingsChannelTwilioFields';
import {SettingsChannelTwitterFields} from './twitter/settingsChannelTwitterFields';

/*
 * Props.
 */

interface SettingsSingleChannelProps {
  channel: ChannelModel;
  inbox: InboxModel;
  url: SettingsInboxesUrl;
  panelId: SettingsPanelId;
}

/*
 * Component.
 */

export const SettingsSingleChannel: FC<SettingsSingleChannelProps> = (props) => {
  const {inbox, url} = props;

  const initialState = useMemo(
    () => ({
      channel: props.channel,
      isDirty: false,
      isEditing: true,
    }),
    [props.channel],
  );

  const dispatch = useAppDispatch();
  const [{channel, isDirty, isEditing}, channelDispatch] = useReducer(channelReducer, initialState);

  const onCancelClick = () => {
    channelDispatch({type: ChannelActionTypesEnum.RESET_STATE, payload: initialState});
  };

  const onSaveClick = async () => {
    const newChannel = await dispatch(updateChannelAsyncAction(channel));
    channelDispatch({
      type: ChannelActionTypesEnum.RESET_STATE,
      payload: {...initialState, channel: newChannel},
    });
  };

  const onPreferencesChange: HandlerOf<Partial<ChannelModel['preferences']>> = (payload) => {
    channelDispatch({type: ChannelActionTypesEnum.UPDATE_PREFERENCES, payload});
  };
  const onFieldsChange: HandlerOf<Partial<ChannelModel>> = (payload) => {
    channelDispatch({type: ChannelActionTypesEnum.UPDATE_FIELDS, payload});
  };

  const nameField = (
    <StyledSettingsFormField>
      <StyledSettingsFormTextBox
        $width={224}
        label={<Trans id="channeledited-name">Channel name</Trans>}
        value={channel.displayName}
        onChange={(newName) => {
          onFieldsChange({
            displayName: newName,
          });
        }}
        isRequired
        size={VisualSizesEnum.MEDIUM}
      />
    </StyledSettingsFormField>
  );

  const renderContent = () => {
    switch (channel.type) {
      case ChannelTypesEnum.FRONT_CHAT: {
        return (
          <>
            {nameField}
            <SettingsFrontChatFields
              url={url}
              edited={channel}
              onChange={onFieldsChange}
              onPreferencesChange={onPreferencesChange}
              setDirty={() => channelDispatch({type: ChannelActionTypesEnum.SET_DIRTY})}
            />
          </>
        );
      }

      case ChannelTypesEnum.SMTP: {
        return (
          <SettingsSmtpFields
            url={url}
            edited={channel}
            onChange={onFieldsChange}
            onPreferencesChange={onPreferencesChange}
            setDirty={() => channelDispatch({type: ChannelActionTypesEnum.SET_DIRTY})}
          />
        );
      }

      case ChannelTypesEnum.GMAIL: {
        return (
          <SettingsGmailFields
            inbox={inbox}
            url={url}
            edited={channel}
            onChange={onFieldsChange}
            onPreferencesChange={onPreferencesChange}
            setDirty={() => channelDispatch({type: ChannelActionTypesEnum.SET_DIRTY})}
          />
        );
      }

      case ChannelTypesEnum.OFFICE365: {
        return (
          <>
            {nameField}
            <SettingsOffice365Fields
              inbox={inbox}
              url={url}
              edited={channel}
              onChange={onFieldsChange}
              onPreferencesChange={onPreferencesChange}
              setDirty={() => channelDispatch({type: ChannelActionTypesEnum.SET_DIRTY})}
            />
          </>
        );
      }

      case ChannelTypesEnum.CUSTOM: {
        return (
          <>
            {nameField}
            <SettingsChannelCustomFields
              inbox={inbox}
              url={url}
              edited={channel}
              onChange={onFieldsChange}
              onPreferencesChange={onPreferencesChange}
              setDirty={() => channelDispatch({type: ChannelActionTypesEnum.SET_DIRTY})}
            />
          </>
        );
      }

      case ChannelTypesEnum.TWILIO: {
        return (
          <>
            {nameField}
            <SettingsChannelTwilioFields
              inbox={inbox}
              url={url}
              edited={channel}
              onChange={onFieldsChange}
              onPreferencesChange={onPreferencesChange}
              setDirty={() => channelDispatch({type: ChannelActionTypesEnum.SET_DIRTY})}
            />
          </>
        );
      }

      case ChannelTypesEnum.SMOOCH: {
        return (
          <>
            {nameField}
            <SettingsChannelSmoochFields
              inbox={inbox}
              url={url}
              edited={channel}
              onChange={onFieldsChange}
              onPreferencesChange={onPreferencesChange}
              setDirty={() => channelDispatch({type: ChannelActionTypesEnum.SET_DIRTY})}
            />
          </>
        );
      }

      case ChannelTypesEnum.TWITTER_DM: {
        return (
          <>
            {nameField}
            <SettingsChannelTwitterFields
              inbox={inbox}
              url={url}
              edited={channel}
              onChange={onFieldsChange}
              onPreferencesChange={onPreferencesChange}
              setDirty={() => channelDispatch({type: ChannelActionTypesEnum.SET_DIRTY})}
            />
          </>
        );
      }

      case ChannelTypesEnum.TWITTER: {
        return (
          <SettingsChannelTwitterFields
            inbox={inbox}
            url={url}
            edited={channel}
            onChange={onFieldsChange}
            onPreferencesChange={onPreferencesChange}
            setDirty={() => channelDispatch({type: ChannelActionTypesEnum.SET_DIRTY})}
          />
        );
      }

      default: {
        return null;
      }
    }
  };

  const maybeRenderFooter = () => {
    if (!isDirty) {
      return null;
    }

    return (
      <SettingsFooter>
        <Button variant={ButtonVariantsEnum.SECONDARY} onClick={onCancelClick}>
          <Trans id="settingstags-cancel">Cancel</Trans>
        </Button>
        <Button variant={ButtonVariantsEnum.PRIMARY} onClick={onSaveClick} isDisabled={!isDirty}>
          {isEditing ? (
            <Trans id="settingstags-savetag">Save</Trans>
          ) : (
            <Trans id="settingstags-createtag">Create</Trans>
          )}
        </Button>
      </SettingsFooter>
    );
  };

  return (
    <StyledSettingsWrapper>
      <SettingsInboxesChannelHeader inbox={inbox} url={url} channel={channel} />
      <StyledSettingsFormMain>
        <StyledSettingsFormContent>
          <SettingsChannelWarningBanner panelId={props.panelId} channel={channel} inbox={inbox} />
          {renderContent()}
        </StyledSettingsFormContent>
      </StyledSettingsFormMain>
      {maybeRenderFooter()}
    </StyledSettingsWrapper>
  );
};

/*
 * Helpers.
 */

enum ChannelActionTypesEnum {
  UPDATE_FIELDS = 'UPDATE_FIELD',
  UPDATE_PREFERENCES = 'UPDATE_PREFERENCES',
  SET_DIRTY = 'SET_DIRTY',
  RESET_STATE = 'RESET_STATE',
}

type ChannelUpdateFieldAction<T extends ChannelModel> = {
  type: ChannelActionTypesEnum.UPDATE_FIELDS;
  payload: Partial<Omit<T, 'preferences'>>;
};
type ChannelUpdatePreferencesAction<T extends ChannelModel> = {
  type: ChannelActionTypesEnum.UPDATE_PREFERENCES;
  payload: Partial<T['preferences']>;
};
type ChannelSetDirtyAction = {type: ChannelActionTypesEnum.SET_DIRTY};
type ChannelResetState<T extends ChannelModel> = {
  type: ChannelActionTypesEnum.RESET_STATE;
  payload: SingleChannelState<T>;
};

type ChannelAction<T extends ChannelModel> =
  | ChannelUpdateFieldAction<T>
  | ChannelUpdatePreferencesAction<T>
  | ChannelSetDirtyAction
  | ChannelResetState<T>;

interface SingleChannelState<T> {
  channel: T;
  isDirty: boolean;
  isEditing: boolean;
}

function channelReducer<T extends ChannelModel>(
  state: SingleChannelState<T>,
  action: ChannelAction<T>,
): SingleChannelState<T> {
  switch (action.type) {
    case ChannelActionTypesEnum.SET_DIRTY: {
      return {
        ...state,
        isDirty: true,
      };
    }
    case ChannelActionTypesEnum.UPDATE_FIELDS: {
      return {
        ...state,
        isDirty: true,
        channel: {
          ...state.channel,
          ...action.payload,
        },
      };
    }
    case ChannelActionTypesEnum.UPDATE_PREFERENCES: {
      return {
        ...state,
        isDirty: true,
        channel: {
          ...state.channel,
          preferences: {
            ...state.channel.preferences,
            ...action.payload,
          },
        },
      };
    }

    case ChannelActionTypesEnum.RESET_STATE: {
      return action.payload;
    }

    default: {
      return state;
    }
  }
}
